/**
 * Business Term Service
 * API client for business glossary term management
 */
import api from './api';

export const API_BASE_URL = 'http://localhost:3002/api/v1';
export const getBusinessTerms = async () => {
  const response = await fetch(`${API_BASE_URL}/business-terms`);
  return response.json(); 
};

// TypeScript Interfaces
export interface BusinessTerm {
  _id: string;
  name: string;
  definition: string;
  acronym?: string;
  synonyms?: string[];
  category: string;
  domain?: {
    _id: string;
    name: string;
    description?: string;
  };
  owner?: {
    _id: string;
    name: string;
    email: string;
  } | null;
  stewards?: Array<{
    _id: string;
    name: string;
    email: string;
  }>;
  status: 'draft' | 'pending-review' | 'approved' | 'deprecated' | 'archived';
  approvalStatus?: {
    requestedBy?: {
      _id: string;
      name: string;
      email: string;
    };
    requestedAt?: string;
    approvedBy?: {
      _id: string;
      name: string;
      email: string;
    };
    approvedAt?: string;
    rejectedBy?: {
      _id: string;
      name: string;
      email: string;
    };
    rejectedAt?: string;
    rejectionReason?: string;
    comments?: string;
  };
  relatedTerms?: Array<{
    _id: string;
    termId: {
      _id: string;
      name: string;
      definition: string;
      status: string;
    };
    relationshipType: 'parent' | 'child' | 'part-of' | 'contains' | 'related' | 'synonym' | 'antonym' | 'see-also';
    description?: string;
    createdAt: string;
  }>;
  linkedAssets?: Array<{
    _id: string;
    assetId: {
      _id: string;
      name: string;
      type: string;
      domain: string;
      status: string;
    };
    linkType: 'implements' | 'uses' | 'defines' | 'references' | 'related';
    description?: string;
    createdAt: string;
    createdBy?: {
      _id: string;
      name: string;
      email: string;
    };
  }>;
  version: string;
  versionHistory?: Array<{
    version: string;
    definition: string;
    changedBy: {
      _id: string;
      name: string;
      email: string;
    };
    changeDate: string;
    changeDescription?: string;
    changeType: 'created' | 'updated' | 'approved' | 'deprecated' | 'restored';
  }>;
  tags?: string[];
  externalReferences?: Array<{
    title: string;
    url: string;
    description?: string;
  }>;
  examples?: string[];
  notes?: string;
  usageCount?: number;
  lastAccessedAt?: string;
  lastAccessedBy?: {
    _id: string;
    name: string;
    email: string;
  };
  customAttributes?: Record<string, any>;
  complianceRelevant?: boolean;
  regulatoryReferences?: Array<{
    regulation: string;
    section: string;
    description?: string;
  }>;
  
  // Unidatum-Standard Fields
  privacyClassification?: 'Public' | 'Internal' | 'Confidential' | 'Highly Confidential' | 'PII' | 'PHI' | 'PCI' | 'Restricted';
  sensitivityLevel?: 'Low' | 'Medium' | 'High' | 'Critical';
  dataPrivacyImpact?: {
    gdprRelevant?: boolean;
    ccpaRelevant?: boolean;
    hipaaRelevant?: boolean;
    pciRelevant?: boolean;
    privacyImpactAssessmentRequired?: boolean;
    retentionPeriod?: string;
    rightToErasure?: boolean;
  };
  dataElementLabel?: string;
  standardizedFormat?: {
    dataType?: 'String' | 'Number' | 'Date' | 'Boolean' | 'Currency' | 'Percentage' | 'Email' | 'Phone' | 'Address' | 'Other';
    format?: string;
    length?: number;
    precision?: number;
    scale?: number;
  };
  businessRules?: Array<{
    ruleId?: string;
    ruleName?: string;
    ruleDescription?: string;
    ruleType?: 'Validation' | 'Calculation' | 'Derivation' | 'Constraint' | 'Business Logic' | 'Other';
    ruleExpression?: string;
    examples?: string[];
    enforcementLevel?: 'Mandatory' | 'Recommended' | 'Optional';
    createdBy?: {
      _id: string;
      name: string;
      email: string;
    };
    createdAt?: string;
  }>;
  permissibleValues?: {
    hasControlledVocabulary?: boolean;
    allowedValues?: Array<{
      value: string;
      label?: string;
      description?: string;
      code?: string;
      sortOrder?: number;
      isActive?: boolean;
      effectiveDate?: string;
      expirationDate?: string;
    }>;
    valueSource?: string;
    allowOtherValues?: boolean;
  };
  
  lastReviewDate?: string;
  nextReviewDate?: string;
  reviewFrequencyDays?: number;
  createdAt: string;
  updatedAt: string;
}

export interface BusinessTermCreateInput {
  name: string;
  definition: string;
  acronym?: string;
  synonyms?: string[];
  category: string;
  domain?: string;
  owner?: string;
  stewards?: string[];
  tags?: string[];
  examples?: string[];
  notes?: string;
  externalReferences?: Array<{
    title: string;
    url: string;
    description?: string;
  }>;
  complianceRelevant?: boolean;
  regulatoryReferences?: Array<{
    regulation: string;
    section: string;
    description?: string;
  }>;
  reviewFrequencyDays?: number;
}

export interface BusinessTermUpdateInput {
  name?: string;
  definition?: string;
  acronym?: string;
  synonyms?: string[];
  category?: string;
  domain?: string;
  stewards?: string[];
  tags?: string[];
  examples?: string[];
  notes?: string;
  changeDescription?: string;
  externalReferences?: Array<{
    title: string;
    url: string;
    description?: string;
  }>;
  complianceRelevant?: boolean;
  regulatoryReferences?: Array<{
    regulation: string;
    section: string;
    description?: string;
  }>;
  reviewFrequencyDays?: number;
  
  // Unidatum-Standard Fields
  privacyClassification?: 'Public' | 'Internal' | 'Confidential' | 'Highly Confidential' | 'PII' | 'PHI' | 'PCI' | 'Restricted';
  sensitivityLevel?: 'Low' | 'Medium' | 'High' | 'Critical';
  dataElementLabel?: string;
  dataPrivacyImpact?: {
    gdprRelevant?: boolean;
    ccpaRelevant?: boolean;
    hipaaRelevant?: boolean;
    pciRelevant?: boolean;
    privacyImpactAssessmentRequired?: boolean;
    retentionPeriod?: string;
    rightToErasure?: boolean;
  };
}

export interface BusinessTermFilters {
  search?: string;
  status?: string | string[];
  category?: string | string[];
  domain?: string;
  owner?: string;
  page?: number;
  limit?: number;
  sort?: string;
}

export interface BusinessTermStatistics {
  byStatus: Array<{ _id: string; count: number }>;
  byCategory: Array<{ _id: string; count: number }>;
  totalTerms: Array<{ count: number }>;
  needingReview: Array<{ count: number }>;
}

export interface PaginatedResponse<T> {
  success: boolean;
  count: number;
  total: number;
  pagination?: {
    next?: { page: number; limit: number };
    prev?: { page: number; limit: number };
  };
  data: T[];
}

export interface SingleResponse<T> {
  success: boolean;
  data: T;
}

// Business Term Service Class
class BusinessTermService {
  private baseUrl = '/business-terms';

  /**
   * Get all business terms with optional filters
   */
  async getBusinessTerms(filters?: BusinessTermFilters): Promise<PaginatedResponse<BusinessTerm>> {
    const params = new URLSearchParams();
    
    if (filters) {
      if (filters.search) params.append('search', filters.search);
      if (filters.status) {
        if (Array.isArray(filters.status)) {
          filters.status.forEach(s => params.append('status', s));
        } else {
          params.append('status', filters.status);
        }
      }
      if (filters.category) {
        if (Array.isArray(filters.category)) {
          filters.category.forEach(c => params.append('category', c));
        } else {
          params.append('category', filters.category);
        }
      }
      if (filters.domain) params.append('domain', filters.domain);
      if (filters.owner) params.append('owner', filters.owner);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.sort) params.append('sort', filters.sort);
    }
    
    const queryString = params.toString();
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;
    
    const response = await api.get<PaginatedResponse<BusinessTerm>>(url);
    return response.data;
  }

  /**
   * Get a single business term by ID
   */
  async getBusinessTerm(id: string): Promise<SingleResponse<BusinessTerm>> {
    const response = await api.get<SingleResponse<BusinessTerm>>(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Create a new business term
   */
  async createBusinessTerm(data: BusinessTermCreateInput): Promise<SingleResponse<BusinessTerm>> {
    const response = await api.post<SingleResponse<BusinessTerm>>(this.baseUrl, data);
    return response.data;
  }

  /**
   * Update an existing business term
   */
  async updateBusinessTerm(id: string, data: BusinessTermUpdateInput): Promise<SingleResponse<BusinessTerm>> {
    const response = await api.put<SingleResponse<BusinessTerm>>(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a business term
   */
  async deleteBusinessTerm(id: string): Promise<SingleResponse<{}>> {
    const response = await api.delete<SingleResponse<{}>>(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Add a related term
   */
  async addRelatedTerm(
    id: string,
    termId: string,
    relationshipType: 'parent' | 'child' | 'part-of' | 'contains' | 'related' | 'synonym' | 'antonym' | 'see-also',
    description?: string
  ): Promise<SingleResponse<BusinessTerm>> {
    const response = await api.post<SingleResponse<BusinessTerm>>(
      `${this.baseUrl}/${id}/relationships`,
      { termId, relationshipType, description }
    );
    return response.data;
  }

  /**
   * Remove a related term
   */
  async removeRelatedTerm(id: string, relationshipId: string): Promise<SingleResponse<BusinessTerm>> {
    const response = await api.delete<SingleResponse<BusinessTerm>>(
      `${this.baseUrl}/${id}/relationships/${relationshipId}`
    );
    return response.data;
  }

  /**
   * Link term to a data asset
   */
  async linkToAsset(
    id: string,
    assetId: string,
    linkType: 'implements' | 'uses' | 'defines' | 'references' | 'related',
    description?: string
  ): Promise<SingleResponse<BusinessTerm>> {
    const response = await api.post<SingleResponse<BusinessTerm>>(
      `${this.baseUrl}/${id}/assets`,
      { assetId, linkType, description }
    );
    return response.data;
  }

  /**
   * Unlink term from a data asset
   */
  async unlinkFromAsset(id: string, linkId: string): Promise<SingleResponse<BusinessTerm>> {
    const response = await api.delete<SingleResponse<BusinessTerm>>(
      `${this.baseUrl}/${id}/assets/${linkId}`
    );
    return response.data;
  }

  /**
   * Request approval for a term
   */
  async requestApproval(id: string): Promise<SingleResponse<BusinessTerm>> {
    const response = await api.post<SingleResponse<BusinessTerm>>(
      `${this.baseUrl}/${id}/request-approval`
    );
    return response.data;
  }

  /**
   * Approve a term
   */
  async approveTerm(id: string, comments?: string): Promise<SingleResponse<BusinessTerm>> {
    const response = await api.post<SingleResponse<BusinessTerm>>(
      `${this.baseUrl}/${id}/approve`,
      { comments }
    );
    return response.data;
  }

  /**
   * Reject a term
   */
  async rejectTerm(id: string, reason: string): Promise<SingleResponse<BusinessTerm>> {
    const response = await api.post<SingleResponse<BusinessTerm>>(
      `${this.baseUrl}/${id}/reject`,
      { reason }
    );
    return response.data;
  }

  /**
   * Deprecate a term
   */
  async deprecateTerm(id: string, reason?: string): Promise<SingleResponse<BusinessTerm>> {
    const response = await api.post<SingleResponse<BusinessTerm>>(
      `${this.baseUrl}/${id}/deprecate`,
      { reason }
    );
    return response.data;
  }

  /**
   * Get terms needing review
   */
  async getTermsNeedingReview(): Promise<PaginatedResponse<BusinessTerm>> {
    const response = await api.get<PaginatedResponse<BusinessTerm>>(
      `${this.baseUrl}/review/needed`
    );
    return response.data;
  }

  /**
   * Get business term statistics
   */
  async getStatistics(): Promise<SingleResponse<BusinessTermStatistics>> {
    const response = await api.get<SingleResponse<BusinessTermStatistics>>(
      `${this.baseUrl}/statistics`
    );
    return response.data;
  }
}

// Export singleton instance
export default new BusinessTermService();
